import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import MuiButton from "@mui/material/Button"
import type { ButtonProps as MuiButtonProps } from "@mui/material/Button"
import { alpha, type SxProps, type Theme } from "@mui/material/styles"

import { cn } from "@/lib/utils"

/** Applied to disabled `Button` (Tailwind + MUI) so the whole control reads clearly inactive. */
const DISABLED_BUTTON_OPACITY = 0.58

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-inter font-semibold ring-offset-background transition-[color,background-color,border-color,opacity] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-[0.58] [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-ds-primary text-white hover:bg-ds-primary-hover active:bg-ds-primary-active",
        primary:
          "bg-ds-primary text-white hover:bg-ds-primary-hover active:bg-ds-primary-active",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-ds-primary bg-transparent text-ds-primary hover:bg-ds-surface-blue hover:text-ds-primary active:bg-ds-surface-blue-soft disabled:bg-transparent disabled:border-slate-300 disabled:text-slate-400",
        secondary:
          "bg-ds-secondary text-white hover:bg-ds-secondary-hover active:bg-ds-secondary-active",
        iconText:
          "bg-ds-primary text-white hover:bg-ds-primary-hover active:bg-ds-primary-active",
        ghost:
          "hover:bg-accent hover:text-accent-foreground disabled:bg-transparent disabled:text-slate-400",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2 text-sm",
        sm: "h-9 rounded-md px-3 text-sm",
        lg: "h-11 rounded-md px-8 text-base",
        small: "h-11 min-w-[9rem] px-6 text-body-sm sm:min-w-[180px]",
        normal: "h-12 min-w-[10rem] px-7 text-body sm:min-w-[220px]",
        medium: "h-14 min-w-[11rem] px-8 text-body-md sm:min-w-[260px]",
        large: "h-16 min-w-[12rem] px-10 text-body-lg sm:min-w-[300px]",
        icon: "h-10 w-10",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      fullWidth: false,
    },
  }
)

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

type ButtonVariant = NonNullable<ButtonProps["variant"]>
type ButtonSize = NonNullable<ButtonProps["size"]>

const muiVariantMap = {
  default: "contained",
  primary: "contained",
  destructive: "contained",
  outline: "outlined",
  secondary: "contained",
  iconText: "contained",
  ghost: "text",
  link: "text",
} as const satisfies Record<ButtonVariant, MuiButtonProps["variant"]>

const muiColorMap = {
  default: "primary",
  primary: "primary",
  destructive: "error",
  outline: "primary",
  secondary: "secondary",
  iconText: "primary",
  ghost: "primary",
  link: "primary",
} as const satisfies Record<ButtonVariant, MuiButtonProps["color"]>

const muiSizeMap = {
  default: "medium",
  sm: "small",
  lg: "large",
  small: "medium",
  normal: "medium",
  medium: "large",
  large: "large",
  icon: "small",
} as const satisfies Record<ButtonSize, MuiButtonProps["size"]>

const customSizeStyles: Partial<Record<ButtonSize, SxProps<Theme>>> = {
  small: { minWidth: { xs: 144, sm: 180 }, minHeight: 44, px: 3 },
  normal: { minWidth: { xs: 160, sm: 220 }, minHeight: 48, px: 3.5 },
  medium: { minWidth: { xs: 176, sm: 260 }, minHeight: 56, px: 4 },
  large: { minWidth: { xs: 192, sm: 300 }, minHeight: 64, px: 5 },
  icon: { minWidth: 40, width: 40, height: 40, p: 0 },
}

function disabledButtonSx(variant: ButtonVariant, theme: Theme): SxProps<Theme> {
  const disabledText = theme.palette.text.disabled
  switch (variant) {
    case "outline":
      return {
        "&.Mui-disabled": {
          opacity: DISABLED_BUTTON_OPACITY,
          backgroundColor: "transparent",
          borderColor: disabledText,
          color: disabledText,
          WebkitTextFillColor: disabledText,
        },
      }
    case "default":
    case "primary":
    case "iconText":
      return {
        "&.Mui-disabled": {
          opacity: DISABLED_BUTTON_OPACITY,
          backgroundColor: alpha(theme.palette.primary.main, 0.42),
          color: alpha(
            theme.palette.primary.contrastText ?? theme.palette.common.white,
            0.9
          ),
        },
      }
    case "secondary":
      return {
        "&.Mui-disabled": {
          opacity: DISABLED_BUTTON_OPACITY,
          backgroundColor: alpha(theme.palette.secondary.main, 0.42),
          color: alpha(
            theme.palette.secondary.contrastText ?? theme.palette.common.white,
            0.9
          ),
        },
      }
    case "destructive":
      return {
        "&.Mui-disabled": {
          opacity: DISABLED_BUTTON_OPACITY,
          backgroundColor: alpha(theme.palette.error.main, 0.42),
          color: alpha(
            theme.palette.error.contrastText ?? theme.palette.common.white,
            0.9
          ),
        },
      }
    case "ghost":
    case "link":
      return {
        "&.Mui-disabled": {
          opacity: DISABLED_BUTTON_OPACITY,
          backgroundColor: "transparent",
          color: disabledText,
          WebkitTextFillColor: disabledText,
        },
      }
    default:
      return {}
  }
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, fullWidth, asChild = false, ...props }, ref) => {
    const resolvedVariant: ButtonVariant = variant ?? "default"
    const resolvedSize: ButtonSize = size ?? "default"
    const classes = cn(
      buttonVariants({
        variant: resolvedVariant,
        size: resolvedSize,
        fullWidth,
        className,
      })
    )

    const Comp = asChild ? Slot : "button"
    if (asChild) {
      return <Comp className={classes} ref={ref} {...props} />
    }

    const sx: SxProps<Theme> = (theme) => ({
      ...(customSizeStyles[resolvedSize] ?? {}),
      ...(resolvedVariant === "link"
        ? { minWidth: 0, p: 0, justifyContent: "flex-start" }
        : {}),
      ...(resolvedVariant === "ghost"
        ? { minWidth: 0, boxShadow: "none" }
        : {}),
      "&:hover": {
        boxShadow: "none",
      },
      ...disabledButtonSx(resolvedVariant, theme),
    })

    return (
      <MuiButton
        className={classes}
        color={muiColorMap[resolvedVariant]}
        fullWidth={!!fullWidth}
        size={muiSizeMap[resolvedSize]}
        sx={sx}
        variant={muiVariantMap[resolvedVariant]}
        ref={ref}
        type={props.type ?? "button"}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
